import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Signature } from '@shared/data/entity/fields/SignatureField';
import { useSelector } from '@shared/store';
import { API } from '@shared/types/api';
import { FileUpload } from '@shared/types/fieldValues';
import { containedImageUrl, fullSizeImageUri, greyscaleImageUri } from '@shared/util/media/image';
import { getController } from '@shared/util/timeout';

interface Props {
  image: FileUpload | API.ChatAttachmentFragment | Signature;
  containerWidth?: number;
  containerHeight?: number;
  size?: 'full' | 'contain';
  grayscale?: boolean;
  heightOffset?: number;
  widthOffset?: number;
}

interface ImageSrcAndDimensions {
  src: string;
  width: number;
  height: number;
  ratio: number;
  isLandscape: boolean;
  isPortrait: boolean;
  /** True once the browser has confirmed the image's natural dimensions. */
  resolved: boolean;
}

/**
 * Images reach us with no usable density metadata. Imgix strips it while
 * re-encoding, so the bytes the browser decodes no longer say whether 900
 * pixels were meant to be 900 points or 450. We therefore assume every image
 * came from a 2x display.
 *
 * Retina screenshots, by far the most common attachment, then lay out at the
 * size they were captured at. Genuine 1x images render at half their pixel
 * size, which is the accepted cost of not requesting metadata per image.
 */
const ASSUMED_SOURCE_DPR = 2;

/**
 * Given an image object and its container width/height,
 * returns an Imgix src and the width and height of the image.
 *
 * Will return the fullsize image src or an src for the max image size
 * that will fit in the given container dimensions.
 *
 * Display size assumes the source image came from a 2x display, so a retina
 * screenshot lays out at the size it was captured at rather than at twice it.
 *
 * Returns ratio, isLandscape, and isPortrait convenience values.
 */
export function useImageSrcAndDimensions({
  image,
  containerWidth,
  containerHeight,
  size = 'full',
  grayscale = false,
  widthOffset = 0,
  heightOffset = 0,
}: Props): ImageSrcAndDimensions {
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const viewportHeight = useSelector(state => state.appState.viewportHeight);
  const [naturalDimensions, setNaturalDimensions] = useState<[number, number]>();
  const [resolved, setResolved] = useState(false);

  const colorUrl = grayscale ? greyscaleImageUri(image.url) : image.url;
  const src =
    size === 'full'
      ? fullSizeImageUri(colorUrl)
      : containedImageUrl(colorUrl, [containerWidth ?? 0, containerHeight ?? 0]);

  // Load the image in the browser to get post-EXIF naturalWidth/naturalHeight.
  useEffect(() => {
    const { signal, abort } = getController();
    const img = new window.Image();

    img.addEventListener(
      'load',
      () => {
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        if (nw > 0 && nh > 0) {
          setNaturalDimensions([nw, nh]);
        }
        setResolved(true);
      },
      { signal },
    );

    setResolved(false);
    img.src = src;

    return abort;
  }, [src]);

  // Layout comes from the source image's own pixel dimensions, never from the
  // file at `src`: in "contain" mode imgix has already resized that file to the
  // container times the viewer's screen dpr, so measuring it would fold the
  // viewer's screen density into the layout.
  //
  // The browser's post-EXIF natural dimensions still decide which way round the
  // image is, because the server can report raw sensor dimensions that ignore
  // the rotation the browser applies (common with iOS camera uploads). They are
  // also the fallback when the server reports no dimensions at all.
  const intrinsicDimensions = useMemo<[number | undefined, number | undefined]>(() => {
    const reported: [number | undefined, number | undefined] = [
      image.width ?? undefined,
      image.height ?? undefined,
    ];
    if (isNil(naturalDimensions)) {
      return reported;
    }
    if (isNil(reported[0]) || isNil(reported[1])) {
      return naturalDimensions;
    }
    const reportedIsLandscape = reported[0] >= reported[1];
    const naturalIsLandscape = naturalDimensions[0] >= naturalDimensions[1];
    return reportedIsLandscape === naturalIsLandscape ? reported : [reported[1], reported[0]];
  }, [image.width, image.height, naturalDimensions]);

  const ratio = (intrinsicDimensions[0] ?? 1) / (intrinsicDimensions[1] ?? 1);
  const isLandscape = ratio >= 1;
  const isPortrait = ratio < 1;

  // Compute display dimensions synchronously so the container never starts at
  // the 56×56 minimum before jumping to its real size. This prevents the layout
  // shift that causes the scroll view to over-adjust when images first appear.
  const dimensions = useMemo(() => {
    const [w, h] = intrinsicDimensions;
    if (isNil(w) || isNil(h)) {
      return [undefined, undefined];
    }
    const maxWidth = !isNil(containerWidth) ? containerWidth : viewportWidth;
    const maxHeight = !isNil(containerHeight) ? containerHeight : viewportHeight;
    if (isNil(maxWidth) || isNil(maxHeight)) {
      return [undefined, undefined];
    }
    // The size the image asks to be drawn at, before it has to fit anywhere.
    const naturalW = w / ASSUMED_SOURCE_DPR;
    const naturalH = h / ASSUMED_SOURCE_DPR;
    // "contain" scaling: use the most restrictive scale so image fits both dimensions
    const scale = Math.min(maxWidth / naturalW, maxHeight / naturalH, 1);
    return [naturalW * scale + widthOffset, naturalH * scale + heightOffset];
  }, [
    intrinsicDimensions,
    containerWidth,
    containerHeight,
    widthOffset,
    heightOffset,
    viewportWidth,
    viewportHeight,
  ]);

  return {
    src,
    width: dimensions[0] ?? 0,
    height: dimensions[1] ?? 0,
    ratio,
    isLandscape,
    isPortrait,
    resolved,
  };
}
